import { Paperclip } from "lucide-react";
import { useState } from "react";
import { DropzoneOptions } from "react-dropzone";
import { toast } from "sonner";
import { IoClose } from "react-icons/io5";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 } from "uuid";

import { storage } from "@/firebase";

import {
  FileUploader,
  FileUploaderContent,
  FileUploaderItem,
  FileInput,
} from "@/components/extensions/file-upload";
import { Button } from "@/components/ui/button/button";

const FileSvgDraw = () => {
  return (
    <>
      <svg
        className="w-8 h-8 mb-3 text-gray-500 dark:text-gray-400"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 20 16"
      >
        <path
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
        />
      </svg>
      <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">
        <span className="font-semibold">Click to upload</span>
        &nbsp; or drag and drop
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Image or PDF file only
      </p>
    </>
  );
};

interface UploadAttachmentProps {
  value: string | undefined;
  onChange: (value: string) => void;
}

export const UploadAttachment = ({
  value,
  onChange,
}: UploadAttachmentProps) => {
  const [files, setFiles] = useState<File[] | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const FileExtension = value?.split("?")?.[0]?.split(".")?.at(-1);

  const handleAttachmentUpload = async () => {
    if (files === null || files.length === 0) {
      return toast.error("Please Select a file first!");
    }

    setIsUploading(true);
    const loadingToast = toast.loading("Uploading...");

    try {
      const file = files[0];
      const Ref = ref(
        storage,
        `allowance_request_attachments/${v4() + "~" + file.name}`
      );

      const submitResp = await uploadBytes(Ref, file);

      const url = await getDownloadURL(submitResp.ref);

      onChange(url);
      setFiles(null);

      toast.success("File uploaded!", {
        id: loadingToast,
      });
    } catch (error) {
      toast.error("Something went wrong!");
      console.log(
        "ERROR WHILE UPLOADING ATTACHMENT IN LEAVE APPLICATION",
        error
      );
      return null;
    } finally {
      toast.dismiss(loadingToast);
      setIsUploading(false);
    }
  };

  const dropZoneConfig: DropzoneOptions = {
    maxFiles: 1,
    maxSize: 1024 * 1024 * 4,
    multiple: false,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg"],
      "application/pdf": [".pdf"],
    },
  };

  if (value && FileExtension === "pdf") {
    const hurr = value?.split("?")?.[0]?.split(".")?.at(-2)?.split("~")?.at(-1);

    return (
      <div className="p-1 relative">
        <div className="relative w-auto max-w-lg rounded-md border p-3">
          <button
            className="absolute -right-3 -top-3 bg-red-500 rounded-full p-1"
            type="button"
            onClick={() => {
              onChange("");
            }}
          >
            <IoClose className="w-4 h-4" />
          </button>
          <h4 className="flex flex-row items-center gap-2">
            <code className="bg-accent px-3 rounded-md">
              {hurr !== "" ? `${hurr}.pdf` : "Your pdf file"}
            </code>
            <span>uploaded!</span>
          </h4>
        </div>
      </div>
    );
  }

  if (value && ["png", "jpg", "jpeg"].includes(FileExtension as string)) {
    return (
      <div className="p-1 relative">
        <div className="relative w-72 rounded-md">
          <button
            className="absolute -right-3 -top-3 bg-red-500 rounded-full p-1"
            type="button"
            onClick={() => {
              onChange("");
            }}
          >
            <IoClose className="w-4 h-4" />
          </button>
          <img
            src={value}
            alt="Uploaded Image"
            className="object-cover max-w-full rounded-md"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 max-w-lg">
      <FileUploader
        value={files}
        onValueChange={setFiles}
        dropzoneOptions={dropZoneConfig}
        className="relative bg-background rounded-lg p-2"
      >
        <FileInput className="outline-dashed outline-1 outline-white">
          <div className="flex items-center justify-center flex-col pt-3 pb-4 w-full ">
            <FileSvgDraw />
          </div>
        </FileInput>
        <FileUploaderContent>
          {files &&
            files.length > 0 &&
            files.map((file, i) => (
              <FileUploaderItem key={i} index={i}>
                <Paperclip className="h-4 w-4 stroke-current" />
                <span>{file.name}</span>
              </FileUploaderItem>
            ))}
        </FileUploaderContent>
      </FileUploader>

      <div className="">
        <Button
          type="button"
          className="w-full"
          disabled={files === null || files.length === 0 || isUploading}
          onClick={handleAttachmentUpload}
        >
          Upload
        </Button>
      </div>
    </div>
  );
};
